import { useCallback, useEffect, useRef, useState } from "react";
import {
  pathnameForCategory,
  pathnameForOrder,
  pathnameForPayment,
  pathnameForProduct,
  pathnameForRoute,
  routeStateFromPathname,
  routeStateKey,
  type AppRoute,
  type RouteState,
} from "../lib/routes";

const routeLoadingDelayMs = 320;

export function useAppRoute() {
  const [routeState, setRouteState] = useState<RouteState>(() => routeStateFromPathname(window.location.pathname));
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const routeLoadingTimerRef = useRef<number | null>(null);

  const transitionToRoute = useCallback((nextRouteState: RouteState) => {
    setRouteState((currentRouteState) => {
      if (routeStateKey(currentRouteState) === routeStateKey(nextRouteState)) {
        return currentRouteState;
      }

      if (routeLoadingTimerRef.current !== null) {
        window.clearTimeout(routeLoadingTimerRef.current);
      }

      setIsRouteLoading(true);
      routeLoadingTimerRef.current = window.setTimeout(() => {
        setIsRouteLoading(false);
        routeLoadingTimerRef.current = null;
      }, routeLoadingDelayMs);

      return nextRouteState;
    });
  }, []);

  const navigateToPathname = useCallback((pathname: string) => {
    const nextRouteState = routeStateFromPathname(pathname);

    setRouteState((currentRouteState) => {
      if (routeStateKey(currentRouteState) === routeStateKey(nextRouteState)) {
        return currentRouteState;
      }

      window.history.pushState(null, "", pathname);

      if (routeLoadingTimerRef.current !== null) {
        window.clearTimeout(routeLoadingTimerRef.current);
      }

      setIsRouteLoading(true);
      routeLoadingTimerRef.current = window.setTimeout(() => {
        setIsRouteLoading(false);
        routeLoadingTimerRef.current = null;
      }, routeLoadingDelayMs);

      return nextRouteState;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (routeLoadingTimerRef.current !== null) {
        window.clearTimeout(routeLoadingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    function handlePopState() {
      transitionToRoute(routeStateFromPathname(window.location.pathname));
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [transitionToRoute]);

  return {
    activeRoute: routeState.route,
    isRouteLoading,
    routeCategory: routeState.category,
    routeOrderCode: routeState.orderCode,
    routeProductId: routeState.productId,
    navigate: (route: AppRoute) => navigateToPathname(pathnameForRoute(route)),
    navigateToCategory: (category: string) => navigateToPathname(pathnameForCategory(category)),
    navigateToOrder: (orderCode: string) => navigateToPathname(pathnameForOrder(orderCode)),
    navigateToPayment: (orderCode: string) => navigateToPathname(pathnameForPayment(orderCode)),
    navigateToProduct: (productId: string) => navigateToPathname(pathnameForProduct(productId)),
  };
}
